import { SeniorityFunnelData } from '../types';
import {
    buildAlumniDirectoryPath,
    resolveSeniorityLevelCode,
} from '../directoryLinks';
import { formatNum } from '../chartSetup';
import SectionCard from './SectionCard';
import SectionStateCard from './SectionStateCard';
import CenteredFunnel from './CenteredFunnel';

// Alumni Seniority Classification — centered funnel with level labels to the right.
interface Props {
    data: SeniorityFunnelData | null;
    error?: string;
    emptyMessage?: string;
    campusId?: string | null;
    enableDirectoryLinks?: boolean;
}

// What each seniority level covers, shown on hover of the level label. Keyed by the
// label stripped to lowercase letters so sheet wording ("Mid Management" vs
// "Mid-Management", "Founders / Entrepreneurs" vs "Founders") still matches.
const LEVEL_DESCRIPTIONS: Record<string, string> = {
    alumniwithprofessionaldetails:
        'Alumni with a designation level set: Manager & Others through Founders',
    managerothers: 'People in Entry level roles up to manager roles',
    midmanagement: 'Team Leads, Department Heads, and people with basic decision making authority',
    seniormanagement: 'AVP, VP, SVP or Business Head, Business Unit Directors, Country Head etc',
    executiveleadership:
        'Executive Directors, Managing Directors, Board Member, Partners, CXOs, Senior Advocates etc',
    founders: 'Founders, Co-Founders, Business Owner etc who own the business or have founded the business.',
};

const descriptionFor = (label: string, notes: string | null): string | null => {
    const key = label.toLowerCase().replace(/[^a-z]/g, '');
    // "Founders", "Founders / Entrepreneurs", "Founders & Entrepreneurs" all map to one entry.
    if (key.startsWith('founder')) return LEVEL_DESCRIPTIONS.founders;
    // Fall back to the notes column from the uploaded sheet when the level is unknown.
    return LEVEL_DESCRIPTIONS[key] ?? notes ?? null;
};

const SeniorityFunnelSection = ({
    data,
    error,
    emptyMessage,
    campusId,
    enableDirectoryLinks = false,
}: Props) => {
    if (error) {
        return (
            <SectionCard title="Alumni Seniority Classification">
                <SectionStateCard variant="error" message={error} />
            </SectionCard>
        );
    }
    if (!data || data.levels.length === 0) {
        return (
            <SectionCard title="Alumni Seniority Classification">
                <SectionStateCard variant="empty" message={emptyMessage} />
            </SectionCard>
        );
    }

    const top = data.levels[0]?.count || 0;
    const items = data.levels.map((l) => {
        let href: string | null = null;
        if (enableDirectoryLinks) {
            const key = l.label.toLowerCase().replace(/[^a-z]/g, '');
            if (key.startsWith('totalalumni')) {
                href = buildAlumniDirectoryPath({ instituteId: campusId });
            } else if (key.startsWith('alumniwithprofessional') || key.includes('professionaldetails')) {
                href = buildAlumniDirectoryPath({
                    professionalDetails: true,
                    instituteId: campusId,
                });
            } else {
                const code = resolveSeniorityLevelCode(l.label);
                href = code
                    ? buildAlumniDirectoryPath({ seniorityLevelCode: code, instituteId: campusId })
                    : null;
            }
        }
        return {
            label: l.label,
            count: l.count,
            pct: l.pct,
            dropPct: l.dropPct,
            description: descriptionFor(l.label, l.notes),
            href,
        };
    });

    return (
        <SectionCard title="Alumni Seniority Classification" badge={`${formatNum(top)} total alumni`}>
            <div className="py-2">
                <CenteredFunnel items={items} labelWidth={34} />
            </div>
        </SectionCard>
    );
};

export default SeniorityFunnelSection;
